import { Box, Flex, font, themedColor } from '@lemonade-hq/cdk';
import { InfoIcon, LeftArrowIcon, Option, OptionsList, RightArrowIcon, Select } from '@lemonade-hq/nuis';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FilingDataQ4, getFinancialsQ4 } from './financials/getFinancialsQ4';
import { DocIcon } from './icons/Icons';
import { MediumSmallTitle } from './shared';

// Types
export interface FilingData {
  accessionNumber: string;
  filingDate: string;
  reportDate: string;
  form: string;
  primaryDocument: string;
  primaryDocDescription: string;
  formattedDescription?: string;
}

// Styled components
const Container = styled.div`
  ${font('main')}
  width: 100%;
  min-width: 100%;
`;

const FilterContainer = styled(Flex)`
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 18px;
  margin-bottom: 32px;

  ${MEDIA_QUERIES.tabletAndDesktop`
    gap: 32px;
  `}
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed; /* Makes columns maintain consistent widths */
`;

const TableHead = styled.thead`
  background-color: ${themedColor('cloud')};
  color: ${themedColor('textPrimary')};
`;

const TableHeader = styled.th`
  ${font('main', { fontWeight: '400', fontSize: '1.6rem' })};
  text-align: left;
  padding: 22px 16px;
  text-transform: capitalize;
  white-space: nowrap;
`;

// Define specific widths for each column
const DateHeader = styled(TableHeader)`
  width: 15%;
`;

const TypeHeader = styled(TableHeader)`
  width: 15%;
`;

const DescriptionHeader = styled(TableHeader)`
  width: 55%;
`;

const ViewHeader = styled(TableHeader)`
  width: 15%;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${themedColor('glass')};

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  ${font('main')};
  padding: 22px 16px;
`;

const DateCell = styled(TableCell)`
  width: 15%;
`;

const FilingTypeCell = styled(TableCell)`
  width: 15%;
`;

const DescriptionCell = styled(TableCell)`
  width: 55%;
`;

const ViewCell = styled(TableCell)`
  width: 15%;
`;

const DownloadLink = styled.a`
  ${font('main', { fontWeight: '400' })};
  color: ${themedColor('primary')};
  text-decoration: none;
  margin-right: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyFilings = styled.div`
  padding: 24px 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

// const TableWrapper = styled.div`
//   border: solid 1px ${themedColor('alto')};
//   border-radius: 10px;
//   overflow: hidden;
//   width: 800px;
//   box-sizing: border-box;
// `;

const PaginationButton = styled.button`
  display: flex;
  border: solid 1px ${themedColor('alto')};
  height: 24px;
  width: 24px;
  border-radius: 4px;
  justify-content: center;
  cursor: pointer;
  align-items: center;
  background-color: ${themedColor('white')};
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.06);
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const selectByFilingTypeOptions = [
  { label: 'All Filing Documents', values: [] },
  { label: 'Annual Filings', values: ['10-K'] },
  { label: 'Quarterly Filings', values: ['10-Q'] },
  { label: 'Current Reports', values: ['8-K'] },
  { label: 'Proxy Filings', values: ['DEFA14A', 'DEF 14A'] },
  {
    label: 'Registration Statements',
    values: ['EFFECT', '424B3', 'S-4/A', 'S-4', 'S-8', 'S-1MEF', 'S-1/A', 'S-1', 'S-3', 'S-3ASR', 'RW'],
  },
  {
    label: 'Section 16 Filing',
    values: ['3', '4', '144'],
  },
];

const FilterTitle = styled.label`
  font-size: 1.6rem;
  text-align: left;
  color: ${themedColor('textPrimary')};
  font-weight: 400;
  white-space: nowrap;
`;

const YearFilter: React.FC<{
  uniqueYears: string[];
  selectedYear: string;
  onChange: (value: string) => void;
}> = ({ uniqueYears, selectedYear, onChange }) => {
  return (
    <Flex alignItems="center" gap="8px" width={['100%', 'auto']}>
      <FilterTitle>By year</FilterTitle>
      <Box width={['100%', '220px']} position="relative">
        <Select value={selectedYear} onChange={onChange} size="medium">
          <OptionsList>
            {uniqueYears.map(year => {
              return (
                <Option key={year} value={year}>
                  {year}
                </Option>
              );
            })}
          </OptionsList>
        </Select>
      </Box>
    </Flex>
  );
};

const DocumentTypeFilter: React.FC<{
  selectedDocumentType: string;
  onChange: (value: string) => void;
}> = ({ selectedDocumentType, onChange }) => {
  return (
    <Flex alignItems="center" gap="8px" width={['100%', 'auto']}>
      <FilterTitle>By type</FilterTitle>
      <Box width={['100%', '220px']} position="relative">
        <Select value={selectedDocumentType} onChange={onChange} size="medium">
          <OptionsList>
            {selectByFilingTypeOptions.map(filingOption => (
              <Option key={filingOption.label} value={JSON.stringify(filingOption.values)}>
                {filingOption.label}
              </Option>
            ))}
          </OptionsList>
        </Select>
      </Box>
    </Flex>
  );
};

// Generate download links for a filing
const getDownloadLinks = (pdfs: string[]) => {
  return (
    <>
      {pdfs.map(url => (
        <DownloadLink href={url} target="_blank" rel="noopener noreferrer" key={url}>
          <DocIcon />
          PDF
        </DownloadLink>
      ))}
    </>
  );
};

const filteredFilingsByYear = (filings: FilingDataQ4['GetEdgarFilingListResult'], year: string) => {
  return filings.filter(filing => new Date(filing.FilingDate).getFullYear().toString() === year);
};

const IconFirstPage = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={9} height={8} fill="none">
    <path
      fill="#9A9A9A"
      fillRule="evenodd"
      d="M3.72 7.53a.75.75 0 0 0 1.06-1.06L2.31 4l2.47-2.47A.75.75 0 0 0 3.72.47l-3 3a.75.75 0 0 0 0 1.06l3 3Zm3.75 0a.75.75 0 0 0 1.06-1.06L6.06 4l2.47-2.47A.75.75 0 0 0 7.47.47l-3 3a.75.75 0 0 0 0 1.06l3 3Z"
      clipRule="evenodd"
    />
  </svg>
);

const IconLastPage = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={9} height={8} fill="none">
    <path
      fill="#9A9A9A"
      fillRule="evenodd"
      d="M1.53.47A.75.75 0 1 0 .47 1.53L2.94 4 .47 6.47a.75.75 0 0 0 1.06 1.06l3-3a.75.75 0 0 0 0-1.06l-3-3Zm3.75 0a.75.75 0 0 0-1.06 1.06L6.69 4 4.22 6.47a.75.75 0 0 0 1.06 1.06l3-3a.75.75 0 0 0 0-1.06l-3-3Z"
      clipRule="evenodd"
    />
  </svg>
);

const PAGINATION_SIZE = 10;

const Pagination: React.FC<{
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}> = ({ totalPages, currentPage, onPageChange }) => {
  const totalCalculatedPages = Math.ceil(totalPages / PAGINATION_SIZE);

  const isPrevDisabled = currentPage === 1;
  const isNextDisabled = currentPage === totalCalculatedPages;

  const paginateNext = () => onPageChange(currentPage + 1);
  const paginatePrev = () => onPageChange(currentPage - 1);
  const paginateFirst = () => onPageChange(1);
  const paginateLast = () => onPageChange(totalCalculatedPages);

  return (
    <Flex justifyContent="space-between" alignItems="center" mt={24} width="100%" px={[20, 20, 0]}>
      <span>
        Page {currentPage}/{totalCalculatedPages}
      </span>

      <Flex gap="8px">
        <PaginationButton onClick={paginateFirst} disabled={isPrevDisabled}>
          <IconFirstPage />
        </PaginationButton>
        <PaginationButton onClick={paginatePrev} disabled={isPrevDisabled}>
          <LeftArrowIcon height={8} width={8} colorOverride="#9A9A9A" />
        </PaginationButton>
        <PaginationButton onClick={paginateNext} disabled={isNextDisabled}>
          <RightArrowIcon height={8} width={8} colorOverride="#9A9A9A" />
        </PaginationButton>
        <PaginationButton onClick={paginateLast} disabled={isNextDisabled}>
          <IconLastPage />
        </PaginationButton>
      </Flex>
    </Flex>
  );
};

const filteredFilingsByFormType = (filings: FilingDataQ4['GetEdgarFilingListResult'], selectedFormType: string[]) => {
  if (selectedFormType.length === 0) return filings;
  return filings.filter(filing => selectedFormType.some(type => type === filing.FilingTypeMnemonic));
};

export const SECFilingsTable: React.FC = () => {
  const [filings, setFilings] = useState<FilingDataQ4['GetEdgarFilingListResult']>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uniqueYears, setUniqueYears] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [selectedFormType, setSelectedFormType] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    void getFinancialsQ4()
      .then(data => {
        if (data.uniqueYears.length === 0) return;
        setSelectedYear(data.uniqueYears[0]);
        setUniqueYears(data.uniqueYears);
        setFilings(data.filings);
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error fetching SEC filings:', error);
        setIsLoading(false);
      });
  }, []);

  const handleYearChange = (value: string) => {
    setSelectedYear(value);
    setCurrentPage(1);
  };

  const handleFormTypeChange = (stringVal: string) => {
    const value = JSON.parse(stringVal);
    setSelectedFormType(value);
    setCurrentPage(1);
  };

  const filteredFilings = useMemo(
    () =>
      filteredFilingsByYear(
        filteredFilingsByFormType(filings, selectedFormType),
        selectedYear ?? new Date().getFullYear().toString(),
      ),
    [filings, selectedFormType, selectedYear],
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (filings.length === 0 || selectedYear == null) {
    return <div>No SEC filings data available.</div>;
  }

  return (
    <Container>
      <Box px={[20, 20, 0]}>
        <MediumSmallTitle>SEC Filing</MediumSmallTitle>
      </Box>
      <FilterContainer px={[20, 20, 0]}>
        <YearFilter uniqueYears={uniqueYears} selectedYear={selectedYear} onChange={handleYearChange} />
        <DocumentTypeFilter selectedDocumentType={JSON.stringify(selectedFormType)} onChange={handleFormTypeChange} />
      </FilterContainer>

      <Flex style={{ overflowX: 'auto' }} px={[20, 20, 0]}>
        <Flex
          minWidth={['800px', '100%', '100%']}
          style={{
            border: `solid 1px #e0e0e0`,
            borderRadius: '10px',
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableHead>
              <tr>
                <DateHeader>Date</DateHeader>
                <TypeHeader>Filing Type</TypeHeader>
                <DescriptionHeader>Filing Description</DescriptionHeader>
                <ViewHeader>View</ViewHeader>
              </tr>
            </TableHead>
            <tbody>
              {filteredFilings.slice((currentPage - 1) * PAGINATION_SIZE, currentPage * PAGINATION_SIZE).map(filing => (
                <TableRow key={filing.FilingId}>
                  <DateCell>{formatDate(filing.FilingDate)}</DateCell>
                  <FilingTypeCell>{filing.FilingTypeMnemonic}</FilingTypeCell>
                  <DescriptionCell>{filing.FilingDescription}</DescriptionCell>
                  <ViewCell>
                    {getDownloadLinks(
                      filing.DocumentList.filter(doc => doc.DocumentType === 'CONVPDF').map(doc => doc.Url),
                    )}
                  </ViewCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </Flex>
      </Flex>

      {filteredFilings.length > 0 && (
        <Pagination
          totalPages={filteredFilings.length}
          currentPage={currentPage}
          onPageChange={page => setCurrentPage(page)}
        />
      )}

      {filteredFilings.length === 0 && (
        <EmptyFilings>
          <InfoIcon />
          No documents were found
        </EmptyFilings>
      )}
    </Container>
  );
};

export default SECFilingsTable;
